<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { isTouchScreen } from '@@/bits/browser'
import { ContentPickerSource } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { getSurfaceAIButtonColors } from '@@/bits/surface_colors'
import { awaitDOMUpdates, awaitTransitionEnd, isHtmlElementAnInputField } from '@@/bits/dom'
import { captureMessage } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { defineAsyncComponent } from '@@/bits/vue'
import type { PageMeta } from '@@/bits/whiteboard'
import {
  actionShortcuts,
  ActionType,
  BackgroundType,
  FRAME_ID_QUERY_PARAM,
  isShapeApproved,
  isShapeChildOfGroup,
  migrateExistingTextShapesToUseThemeFont,
  shouldEnableGroupingForShapes,
  TldrawAppRefKey,
} from '@@/bits/whiteboard'
import { changeAllTldrawDefaultColorStyleColor } from '@@/bits/whiteboard_colors'
import { initializeLinking, listenToLinkingChanges } from '@@/bits/whiteboard_linking'
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '@@/bits/whiteboard_no_dependencies'
import type { TldrawDefaultColorStyleColor } from '@@/bits/whiteboard_tools'
import {
  ARROW_TOOL,
  DRAW_NESTED_TOOL,
  GEO_TOOL,
  INTERACT_TOOL,
  isPadletTool,
  SELECT_TOOL,
  getTldrawToolId,
  TEXT_TOOL,
} from '@@/bits/whiteboard_tools'
import {
  calculateUploadedFilePositionsAndDimensions,
  deleteFailedUploads,
  MAXIMUM_NUMBER_OF_FILES,
} from '@@/bits/whiteboard_upload'
import type { Anchor } from '@@/library/v4/components/OzBasePopover.vue'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceWindowMobileNotchStore } from '@@/pinia/surface_window_mobile_notch'
import { useWhiteboardToolPropertiesStore } from '@@/pinia/useWhiteboardToolPropertiesStore'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardAiStore } from '@@/pinia/whiteboard_ai'
import { useWhiteboardAttachmentsStore, WHITEBOARD_ATTACHMENTS_LOADED_ID } from '@@/pinia/whiteboard_attachments'
import { useWhiteboardCardTemplatesStore } from '@@/pinia/whiteboard_card_templates'
import { useWhiteboardCollaborationStore } from '@@/pinia/whiteboard_collaboration'
import { useWhiteboardElementModifierStore } from '@@/pinia/whiteboard_element_modifier'
import { useWhiteboardFrameBackgroundStore } from '@@/pinia/whiteboard_frame_background'
import { useWhiteboardLinkingStore } from '@@/pinia/whiteboard_linking'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardObjectPreviewStore } from '@@/pinia/whiteboard_object_preview'
import { useWhiteboardPanelsStore } from '@@/pinia/whiteboard_panels'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardScriptStore } from '@@/pinia/whiteboard_script'
import { useWhiteboardSpecialEffectsStore } from '@@/pinia/whiteboard_special_effects'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import { useWhiteboardUploadStore } from '@@/pinia/whiteboard_upload'
import { useWhiteboardZoomLevelStore } from '@@/pinia/whiteboard_zoom_level'
import { BreakPoints, useWindowSizeStore } from '@@/pinia/window_size'
import WhiteboardCanvasDragAndDropOverlay from '@@/vuecomponents/WhiteboardCanvasDragAndDropOverlay.vue'
import WhiteboardModeSwitcher from '@@/vuecomponents/WhiteboardModeSwitcher.vue'
import { useTldrawCanvas } from '@@/vuecomposables/tldraw_canvas'
import { useWhiteboardActions } from '@@/vuecomposables/use_whiteboard_actions'
import { useWhiteboardAttachmentInteraction } from '@@/vuecomposables/use_whiteboard_attachment_interaction'
import { useWhiteboardFrameActions } from '@@/vuecomposables/use_whiteboard_frame_actions'
import { useWhiteboardInteractToolPrefetchImages } from '@@/vuecomposables/use_whiteboard_interact_tool_prefetch_images'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { useTldrawError } from '@@/vuecomposables/useTldrawError'
import {
  CANVAS_TEMPLATE_PREVIEW_PERSPECTIVE,
  CANVAS_TEMPLATE_PREVIEW_ROTATE_Y_DEGREE,
  useWhiteboardCanvasSize,
} from '@@/vuecomposables/useWhiteboardCanvasSize'
import { useWhiteboardContentPickerDropTargetForElements } from '@@/vuecomposables/useWhiteboardContentPickerDragAndDrop'
import { useWhiteboardDragAndDrop } from '@@/vuecomposables/useWhiteboardDragAndDrop'
import { useWhiteboardMediaSearchResultsCache } from '@@/vuecomposables/useWhiteboardMediaSearchResultsCache'
import { useWhiteboardAIChat } from '@@/vuecomposables/useWhiteboardAIChat'
import { useVirtualKeyboardHeight } from '@@/vuecomposables/virtual_keyboard_height'
import { useWhiteboardCustomEventHandlers } from '@@/vuecomposables/whiteboard_custom_event_handlers'
import { useWhiteboardPageHandlers } from '@@/vuecomposables/whiteboard_page_handlers'
import { useWhiteboardShapeHandlers } from '@@/vuecomposables/whiteboard_shape_handlers'
import vEmitLongPress from '@@/vuedirectives/emit_long_press'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import { useElementBounding } from '@vueuse/core'
import { cloneDeep } from 'es-toolkit'
import { uniqueId } from 'es-toolkit/compat'
import { storeToRefs } from 'pinia'
import type { KeyBindingMap } from 'tinykeys'
import tinykeys from 'tinykeys'
import type { Editor, TLCameraOptions, TLPageId, TLShape, TLShapeId, VecLike } from 'tldraw'
import {
  ArrowShapeArrowheadEndStyle,
  ArrowShapeArrowheadStartStyle,
  Box,
  computed as computedAtom,
  DefaultColorStyle,
  DefaultDashStyle,
  DefaultFillStyle,
  DefaultFontStyle,
  DefaultSizeStyle,
  DefaultTextAlignStyle,
  defaultTldrawOptions,
  GeoShapeGeoStyle,
  react,
  setUserPreferences,
  toRichText,
} from 'tldraw'
import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch, type Ref } from 'vue'
import { useSurfaceAIChatStore } from '@@/pinia/surface_ai_chat_store'

const WhiteboardFrameBarOld = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardFrameBarOld.vue'))

const WhiteboardToolbar = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardToolbar.vue'))

const WhiteboardToolbarMenu = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardToolbarMenu.vue'))

const WhiteboardFrameContextMenu = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardFrameContextMenu.vue'))

const WhiteboardCodeFrameObjectPicker = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardCodeFrameObjectPicker.vue'),
)

const ContentPickerForWhiteboard = defineAsyncComponent(() => import('@@/vuecomponents/ContentPickerForWhiteboard.vue'))

const MembershipTierUpgradeModal = defineAsyncComponent(() => import('@@/vuecomponents/MembershipTierUpgradeModal.vue'))

const WhiteboardStartFromTemplateButton = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardStartFromTemplateButton.vue'),
)
const WhiteboardElementModifierContainer = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardElementModifierContainer.vue'),
)
const WhiteboardElementContextMenu = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardElementContextMenu.vue'),
)
const WhiteboardFrameSharePanel = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardFrameSharePanel.vue'))
const WhiteboardFrameBackgroundPanelOld = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameBackgroundPanelOld.vue'),
)
const WhiteboardFrameBackgroundPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameBackgroundPanel.vue'),
)
const WhiteboardLinkingPanel = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardLinkingPanel.vue'))
const WhiteboardHeader = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardHeader.vue'))
const WhiteboardHeaderOld = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardHeaderOld.vue'))
const WhiteboardPlayModeNavigationBar = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardPlayModeNavigationBar.vue'),
)
const WhiteboardFrameBreakoutLinksPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameBreakoutLinksPanel.vue'),
)
const WhiteboardFrameShareViaAnotherAppPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameShareViaAnotherAppPanel.vue'),
)
const SurfaceAIChatButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAIChatButton.vue'))
const WhiteboardAiChatContainer = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardAiChatContainer.vue'))

const WhiteboardCodeEditor = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardCodeEditor.vue'))
const WhiteboardHeaderFrozenBadge = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardHeaderFrozenBadge.vue'),
)
// TODO: Convert it to static import before Beta launch
const WhiteboardFrameBar = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardFrameBar.vue'))
const WhiteboardFrameBreakoutNavigationBar = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameBreakoutNavigationBar.vue'),
)

const tldrawAppRef = ref<HTMLElement>()
provide(TldrawAppRefKey, tldrawAppRef as Ref<HTMLElement>)

const wallContainerRef = ref()

const whiteboardStore = useWhiteboardStore()
const whiteboardPanelsStore = useWhiteboardPanelsStore()
const whiteboardToolsStore = useWhiteboardToolsStore()
const whiteboardToolPropertiesStore = useWhiteboardToolPropertiesStore()
const whiteboardFrameBackgroundStore = useWhiteboardFrameBackgroundStore()
const { currentFrameId, isLoadingPortraitMobileLayout } = storeToRefs(whiteboardStore)
const whiteboardScriptStore = useWhiteboardScriptStore()
const surfaceStore = useSurfaceStore()
const { isFrozen: isWhiteboardFrozen } = storeToRefs(surfaceStore)
const {
  xWhiteboardHeader,
  xModeSwitcher,
  selectedTool,
  selectedSecondaryTool,
  toolStates,
  geoColorTldrawToolId,
  arrowColorTldrawToolId,
} = storeToRefs(whiteboardStore)
const { isDragging } = useWhiteboardDragAndDrop(tldrawAppRef)
useWhiteboardContentPickerDropTargetForElements(tldrawAppRef)
const whiteboardCardTemplatesStore = useWhiteboardCardTemplatesStore()

const { hasError } = useTldrawError()

const hasTldrawLoaded = ref(false)
const isTransparentBackground = ref(false)

const whiteboardElementModifierStore = useWhiteboardElementModifierStore()
const { xContextMenu, contextMenuAnchor } = storeToRefs(whiteboardElementModifierStore)

const whiteboardAiStore = useWhiteboardAiStore()

const { switchFrame } = useWhiteboardFrameActions()

useWhiteboardInteractToolPrefetchImages()

/**
 * MOBILE LANDSCAPE
 */

/** CANVAS RESIZE AND ZOOM */
function updateMobileNotchOnOrientationChangeAndResize() {
  useSurfaceWindowMobileNotchStore().updateSafeAreaInset()
  if (screen.orientation) {
    useSurfaceWindowMobileNotchStore().updateLandscapeNotch()
  }
  surfaceContainerSizeStore.resizeSurfaceContainer({
    width: window.innerWidth,
    height: window.innerHeight,
  })
}
onMounted(() => {
  useSurfaceWindowMobileNotchStore().updateSafeAreaInset()
  // Some older browsers do not support screen.orientation
  if (screen.orientation) {
    useSurfaceWindowMobileNotchStore().updateLandscapeNotch()
    screen.orientation.addEventListener('change', updateMobileNotchOnOrientationChangeAndResize)
  }

  window.addEventListener('resize', updateMobileNotchOnOrientationChangeAndResize)
})
onUnmounted(() => {
  if (screen.orientation) {
    screen.orientation.removeEventListener('change', updateMobileNotchOnOrientationChangeAndResize)
  }
  window.removeEventListener('resize', updateMobileNotchOnOrientationChangeAndResize)
})
const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const { surfaceContainerWidth, surfaceContainerHeight, isLandscapePhone, isContainerSmallerThanTabletPortrait } =
  storeToRefs(surfaceContainerSizeStore)

const aiButtonColors = computed(() => {
  if (!surfaceStore.skinPrimaryVariantColorAsRGB) return null
  return getSurfaceAIButtonColors({
    backgroundColorInRgb: surfaceStore.skinPrimaryVariantColorAsRGB,
    darkMode: surfaceStore.isSkinPrimaryColorDark,
  })
})

const { canvasWidth, shouldTransformCanvasPerspectiveAngle } = useWhiteboardCanvasSize()
const { isVirtualKeyboardShowing } = useVirtualKeyboardHeight()

const whiteboardAttachmentsStore = useWhiteboardAttachmentsStore()
const whiteboardModeStore = useWhiteboardModeStore()
const { xAiChat } = useWhiteboardAIChat()
const whiteboardZoomLevelStore = useWhiteboardZoomLevelStore()
const { isAllowingZooming } = storeToRefs(whiteboardZoomLevelStore)
const { shouldInteractWithShapeInPlayMode } = useWhiteboardAttachmentInteraction()
const whiteboardUploadStore = useWhiteboardUploadStore()

const canvasTemplatePreviewPaddingStart = computed(() => {
  if (surfaceContainerWidth.value <= BreakPoints.TabletLandscape) {
    return 60
  }
  if (surfaceContainerWidth.value >= BreakPoints.Desktop2XL) {
    return 240
  }
  // Linear interpolation between 60 and 240 based on container width
  return (
    60 + ((surfaceContainerWidth.value - 1024) / (BreakPoints.Desktop2XL - BreakPoints.TabletLandscape)) * (240 - 60)
  )
})

const paddingInlineStart = computed<number>(() => {
  if (!whiteboardPanelsStore.xCardTemplatesPanel || isLandscapePhone.value || isLoadingPortraitMobileLayout.value)
    return 0
  return canvasTemplatePreviewPaddingStart.value
})

const paddingInlineEnd = computed<number>(() => {
  if (!whiteboardPanelsStore.xCardTemplatesPanel || isLoadingPortraitMobileLayout.value) return 0
  if (isLandscapePhone.value) {
    return surfaceContainerWidth.value - canvasWidth.value
  }
  const CANVAS_TEMPLATE_PANEL_WIDTH = 396
  return CANVAS_TEMPLATE_PANEL_WIDTH
})

const insetInlineStart = computed(() => {
  if (
    isAppUsing('removePlayMode') ||
    whiteboardModeStore.isPlayMode ||
    whiteboardModeStore.isScreenshotMode ||
    isLoadingPortraitMobileLayout.value ||
    shouldTransformCanvasPerspectiveAngle.value
  ) {
    return 0
  }

  const FRAME_BAR_WIDTH = 100
  const TOOLBAR_WIDTH = whiteboardToolbarStore.isSingleColumnLayout ? 52 : 92
  return (FRAME_BAR_WIDTH - TOOLBAR_WIDTH) / 2
})

const insetTop = computed(() => {
  if (!isLoadingPortraitMobileLayout.value || whiteboardModeStore.isPlayMode) {
    return 0
  }

  // When the card templates drawer is open on a phone device, we move the card up so it's still
  // fully visible. The calculations below are responsible for that.
  if (whiteboardPanelsStore.xCardTemplatesPanel) {
    const CARD_TEMPLATES_PANEL_HEIGHT = 540
    const HEADER_MARGIN_BOTTOM = 20
    const HEADER_HEIGHT = 72
    const availableHeight =
      surfaceContainerHeight.value - HEADER_HEIGHT - HEADER_MARGIN_BOTTOM - CARD_TEMPLATES_PANEL_HEIGHT
    // Calculate offset to prevent canvas from overlapping with whiteboard header
    const offsetTop = canvasHeight.value >= availableHeight ? canvasHeight.value - availableHeight : 0
    return isContainerSmallerThanTabletPortrait.value
      ? -(CARD_TEMPLATES_PANEL_HEIGHT + HEADER_MARGIN_BOTTOM - offsetTop) / 2
      : 0
  }

  const FRAME_BAR_HEIGHT = !whiteboardStore.isFrameBreakout ? whiteboardStore.frameBarHeight : 0
  const TOOLBAR_HEIGHT = 52
  const SPACE_FROM_TOOLBAR_TO_FRAME_BAR = 8
  const ELEMENT_MODIFIER_HEIGHT = isVirtualKeyboardShowing.value ? 92 : 0
  return -(FRAME_BAR_HEIGHT + ELEMENT_MODIFIER_HEIGHT + TOOLBAR_HEIGHT + SPACE_FROM_TOOLBAR_TO_FRAME_BAR) / 2
})
const canvasHeight = computed(() => {
  const W_H_RATIO = 16 / 9

  return canvasWidth.value / W_H_RATIO
})

const tldrawCameraOptions = computed<Partial<TLCameraOptions>>(() => ({
  isLocked: !isAllowingZooming.value, // lock the camera if we're not allowing zooming
  wheelBehavior: !isAllowingZooming.value ? 'none' : 'pan',
  zoomSteps: !isAllowingZooming.value ? [1] : [1, 2, 4, 8],
  zoomSpeed: !isAllowingZooming.value ? 0 : 1,
  constraints: {
    initialZoom: 'fit-y',
    baseZoom: 'fit-y',
    bounds: { x: 0, y: 0, w: CANVAS_WIDTH, h: CANVAS_HEIGHT },
    padding: { x: 0, y: 0 },
    origin: { x: 0, y: 0 },
    behavior: 'contain',
  },
}))

const centerZoomToBounds = async () => {
  await nextTick()
  const tldrawEditor = useTldrawEditor().editor
  // Needs to be unlocked to use zoomToBounds
  tldrawEditor?.setCameraOptions({
    isLocked: false,
  })
  tldrawEditor?.zoomToBounds(new Box(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT), { inset: 0 })
  tldrawEditor?.setCameraOptions({
    ...tldrawCameraOptions.value,
  })
}

// Problem: The tldraw editor sometimes breaks the viewport bounds when the tab loses focus.
// Example: User switches to a different tab then comes back to whiteboard.
// When user tries to draw at point (0, 0), it will draw at point (20, 20) instead (arbitrary values to illustrate the offset).
// Solution: Whenever the window is focused, call `updateViewportScreenBounds` to reset the viewport.
// Reference: https://discord.com/channels/859816885297741824/1237588159806836786
const forceUpdateViewportScreenBounds = async () => {
  await nextTick()
  const rect = document.getElementById('tldraw-app')?.getBoundingClientRect()
  if (!rect) return
  await centerZoomToBounds()
  const next = new Box(rect.left || rect.x, rect.top || rect.y, Math.max(rect.width, 1), Math.max(rect.height, 1))
  const tldrawEditor = useTldrawEditor().editor
  tldrawEditor?.updateViewportScreenBounds(next)
}

function unselectAllShapesAndSwitchToDefaultTool(): void {
  const tldrawEditor = useTldrawEditor().editor
  const selectedShapes = tldrawEditor?.getSelectedShapes()
  if (selectedShapes && selectedShapes.length > 0) {
    tldrawEditor?.selectNone()
    whiteboardStore.selectDefaultTool()
  }
}

function handleClickingOutsideOfCanvas(): void {
  unselectAllShapesAndSwitchToDefaultTool()
  whiteboardAiStore.hideSearchInputAndCardTemplateButtons()
  // Hide toolbar menu
  whiteboardToolbarStore.hideToolbarMenu()
}

// #region TOOL SHORTCUTS REGISTERING & DEREGISTERING

/**
 * We don't want to trigger tool shortcuts while the user is typing
 */
const isActiveElementAnInputField = (): boolean => {
  const activeElement = document?.activeElement
  return isHtmlElementAnInputField(activeElement as HTMLElement) && activeElement?.getAttribute('readonly') == null
}
const isInputFocused = (): boolean => isActiveElementAnInputField()

/**
 * Checks if keyboard shortcuts should be disabled.
 *
 * This function determines when whiteboard keyboard shortcuts (copy, paste, etc.)
 * should be prevented from executing. Shortcuts are disabled when:
 * - An input field is focused
 * - Any modal or panel is open that should block shortcuts
 *
 * @returns {boolean} true if shortcuts should be disabled, false otherwise
 */
const checkIfShortcutsDisallowed = () =>
  isInputFocused() || xScriptEditor.value || xContextMenu.value || whiteboardPanelsStore.hasWhiteboardPanelOpen

const whiteboardPlayModeNavigationBarRef = ref<typeof WhiteboardPlayModeNavigationBar | null>(null)

const { handleAction } = useWhiteboardActions()

function addShortcutsListener(): () => void {
  /**
   * FROZEN
   */
  if (isWhiteboardFrozen.value && !whiteboardModeStore.isPlayMode) {
    return () => {}
  }

  /**
   * CODE MODE
   */
  if (whiteboardModeStore.isCodeMode) {
    return () => {}
  }

  /**
   * PLAY MODE
   */
  if (whiteboardModeStore.isPlayMode) {
    if (whiteboardStore.isFrameBreakout) {
      // We should not let users navigate to other frames in breakout mode
      return () => {}
    }
    return tinykeys(window, {
      ArrowLeft: () => {
        trackEvent('Whiteboard', 'Pressed ArrowLeft Shortcut', null, null, { context: 'Play mode' })
        void handleAction(ActionType.GoToPreviousPage)
      },
      ArrowRight: () => {
        trackEvent('Whiteboard', 'Pressed ArrowRight Shortcut', null, null, { context: 'Play mode' })
        void handleAction(ActionType.GoToNextPage)
      },
      ArrowUp: () => {
        trackEvent('Whiteboard', 'Pressed ArrowUp Shortcut', null, null, { context: 'Play mode' })
        void handleAction(ActionType.GoToPreviousPage)
      },
      ArrowDown: () => {
        trackEvent('Whiteboard', 'Pressed ArrowDown Shortcut', null, null, { context: 'Play mode' })
        void handleAction(ActionType.GoToNextPage)
      },
      PageUp: () => {
        trackEvent('Whiteboard', 'Pressed PageUp Shortcut', null, null, { context: 'Play mode' })
        void handleAction(ActionType.GoToPreviousPage)
      },
      PageDown: () => {
        trackEvent('Whiteboard', 'Pressed PageDown Shortcut', null, null, { context: 'Play mode' })
        void handleAction(ActionType.GoToNextPage)
      },
      Space: () => {
        // If user is on any UI on navigation bar and they hit the space key,
        // we only want the click event to happen and not the forward shortcut key event
        if (whiteboardPlayModeNavigationBarRef.value?.$el?.contains(document.activeElement)) {
          return
        }
        trackEvent('Whiteboard', 'Pressed Space Shortcut', null, null, { context: 'Play mode' })
        void handleAction(ActionType.GoToNextPage)
      },
    })
  }

  /**
   * DRAW MODE
   */

  const keybindingMap: KeyBindingMap = {}
  const primaryShortcuts = whiteboardToolbarStore.flattenedDisplayedTools
    .filter((tool) => {
      if (tool === undefined) {
        captureMessage('tool is undefined', {
          context: {
            flattenedDisplayedTools: whiteboardToolbarStore.flattenedDisplayedTools,
            allEnabledToolIds: whiteboardToolsStore.allEnabledToolIds,
          },
        })
        return false
      }
      return tool.shortcut
    })
    .map((tool) => tool.shortcut)
  const secondaryShortcuts = whiteboardToolbarStore.flattenedDisplayedTools
    .map((tool) => tool.secondaryMenu?.items ?? [])
    .flat()
    .filter((tool) => tool.shortcut)
    .map((tool) => tool.shortcut)

  keybindingMap.Escape = (event: KeyboardEvent) => {
    // Do not conditionally disable this shortcut otherwise there's no way to dismiss the menu with keys
    if (xContextMenu.value) {
      whiteboardElementModifierStore.hideContextMenu()
    } else if (whiteboardStore.selectedToolId !== SELECT_TOOL.id) {
      // Pressing Esc switches the current tool to Select
      whiteboardStore.selectTool(SELECT_TOOL.id)
      whiteboardToolbarStore.hideToolbarMenu()
    }
    // If already using the select tool, Esc should clear any selected shapes
    // Don't clear if the event is from a Tiptap editor, which should let us go back from editing state
    // to normal select state first.
    else if (!(event.target as HTMLElement).classList.contains('tiptap')) {
      unselectAllShapesAndSwitchToDefaultTool()
    }

    if (
      whiteboardCardTemplatesStore.xSuggestedCardTemplatesRow ||
      whiteboardCardTemplatesStore.xStartFromATemplateButton
    ) {
      whiteboardCardTemplatesStore.dismissCardTemplatesSuggestionUI()
    }
    whiteboardAiStore.hideSearchInputAndCardTemplateButtons()
  }

  actionShortcuts.forEach((shortcut) => {
    keybindingMap[shortcut.kbd] = (event) => {
      if (!checkIfShortcutsDisallowed()) {
        const editor = useTldrawEditor().editor
        const selectedShapes = editor?.getSelectedShapes() ?? []

        // Disable grouping if any shape is pending approval
        if (
          (shortcut.actionType === ActionType.Group || shortcut.actionType === ActionType.Ungroup) &&
          !shouldEnableGroupingForShapes(selectedShapes)
        ) {
          return
        }

        // If user is pressing a keyboard shortcut on selected shapes that aren't allowed to be created,
        // don't perform the action.
        if (
          selectedShapes.length > 0 &&
          !whiteboardElementModifierStore.areSelectedShapesTypesAllowedToBeCreated &&
          [ActionType.Cut, ActionType.Copy, ActionType.Duplicate].includes(shortcut.actionType)
        ) {
          return
        }

        // Disable linking if conditions are not met (same as context menu)
        if (shortcut.actionType === ActionType.Link) {
          const onlySelectedShape = selectedShapes.length === 1 ? selectedShapes[0] : null
          const isPoll =
            onlySelectedShape?.type === 'padlet-attachment-shape' &&
            (onlySelectedShape as PadletAttachmentShape).props.isPoll
          if (
            onlySelectedShape == null ||
            onlySelectedShape.type === 'arrow' ||
            whiteboardStore.isFrameBreakout ||
            isShapeChildOfGroup(onlySelectedShape) ||
            isPoll ||
            shouldInteractWithShapeInPlayMode(onlySelectedShape)
          ) {
            return
          }
        }

        trackEvent('Whiteboard', `Pressed ${shortcut.actionType} Shortcut`, null, null, { context: 'Draw mode' })
        event.preventDefault()
        handleBoardAction(shortcut.actionType)
      }
    }
  })

  primaryShortcuts.forEach((shortcut) => {
    if (!shortcut) {
      return
    }
    keybindingMap[shortcut] ??= () => {
      if (checkIfShortcutsDisallowed()) {
        return
      }
      const tool = whiteboardToolbarStore.flattenedDisplayedTools.find((tool) => tool.shortcut === shortcut)
      if (tool && whiteboardStore.selectedToolId !== tool.id) {
        trackEvent('Whiteboard', `Pressed ${shortcut} Shortcut`, null, null, { context: 'Draw mode' })
        whiteboardStore.selectTool(tool.id)
        // Clear the selected toolbar button since we don't have access to it from a shortcut
        whiteboardToolbarStore.hideToolbarMenu()
      }
    }
  })

  secondaryShortcuts.forEach((shortcut) => {
    if (!shortcut) {
      return
    }
    keybindingMap[shortcut] ??= () => {
      if (checkIfShortcutsDisallowed()) {
        return
      }
      const tool = whiteboardToolbarStore.flattenedDisplayedTools.find((tool) =>
        tool.secondaryMenu?.items.find((item) => item?.shortcut === shortcut),
      )
      if (tool) {
        const secondaryTool = tool.secondaryMenu?.items.find((item) => item?.shortcut === shortcut)
        if (secondaryTool) {
          trackEvent('Whiteboard', `Pressed ${shortcut} Shortcut`, null, null, { context: 'Draw mode' })
          whiteboardStore.selectTool(tool.id)
          // Clear the selected toolbar button since we don't have access to it from a shortcut
          whiteboardToolbarStore.hideToolbarMenu()
          whiteboardStore.selectSecondaryTool(secondaryTool.id)
        }
      }
    }
  })

  keybindingMap.c = () => {
    if (checkIfShortcutsDisallowed()) {
      return
    }
    if (whiteboardStore.currentFrameId == null) {
      return
    }

    trackEvent('Whiteboard', 'Pressed C Shortcut', null, null, { context: 'Draw mode' })
    whiteboardStore.addFrameBelow(whiteboardStore.currentFrameId)
  }

  return tinykeys(window, keybindingMap)
}

let removeShortcutsListener = () => {}

onMounted(async () => {
  window.addEventListener('focus', forceUpdateViewportScreenBounds)
  await nextTick()
  removeShortcutsListener = addShortcutsListener()
})

watch([isWhiteboardFrozen, () => whiteboardModeStore.isPlayMode, () => whiteboardModeStore.isCodeMode], async () => {
  removeShortcutsListener()
  // await nextTick()
  // TODO: Somehow the nextTick causes the listeners to be registered twice.
  removeShortcutsListener = addShortcutsListener()
})

// #endregion

// Setup native bridge listeners
onMounted(() => {
  if (device.app) {
    import('@@/vuecomposables/native_bridge_listeners').then(({ useNativeBridgeListeners }) => {
      useNativeBridgeListeners({ for: 'whiteboard' })
    })
  }
})

onUnmounted(() => {
  window.removeEventListener('focus', forceUpdateViewportScreenBounds)
  removeShortcutsListener()
})

function onTldrawCanvasMounted(editor: Editor) {
  const top = tldrawAppRef.value?.getBoundingClientRect().top ?? 0
  canvasTop.value = top

  /**
   * WATCHERS
   *
   * Call all watchers here to avoid the case where tldraw is not initialized yet but we already ran
   * watcher logic that depends on tldraw being initialized.
   */

  watch(geoColorTldrawToolId, (tldrawColor) => {
    if (tldrawColor == null) {
      return
    }
    editor.setStyleForNextShapes(DefaultColorStyle, tldrawColor)
  })

  watch(arrowColorTldrawToolId, (tldrawColor) => {
    if (tldrawColor == null) {
      return
    }
    editor.setStyleForNextShapes(DefaultColorStyle, tldrawColor)
  })

  watch(
    [selectedTool, selectedSecondaryTool, toolStates],
    () => {
      // Ref: https://linear.app/padlet/issue/PRO-15919/long-term-fix-cannot-change-atoms-during-reaction-cycle
      editor.run(() => {
        const newTool = whiteboardStore.selectedTool

        // Ignore these tools because they don't have a childState in Tldraw
        if (
          newTool == null ||
          isPadletTool(newTool.id) ||
          newTool.id === 'padlet-attachment' ||
          newTool.id === 'padlet-media_search' ||
          newTool.id === 'undo' ||
          newTool.id === 'redo'
        ) {
          return
        }
        const newSecondaryTool = whiteboardStore.selectedSecondaryTool
        const newToolStates = whiteboardStore.toolStates

        if (whiteboardStore.ignoreTldrawTrigger) {
          whiteboardStore.ignoreTldrawTrigger = false
          return
        }
        if (newSecondaryTool?.tldrawToolId === 'padlet-speech-bubble') {
          // Emulate the logic for the geo tool
          editor.setStyleForNextShapes(DefaultSizeStyle, 's1')
          editor.setCurrentTool('padlet-speech-bubble')
          editor.setStyleForNextShapes(DefaultFillStyle, newToolStates.geo.fillStyle ?? 'semi')
          if (geoColorTldrawToolId.value) {
            editor.setStyleForNextShapes(DefaultColorStyle, geoColorTldrawToolId.value)
          }
          editor.setStyleForNextShapes(DefaultFontStyle, whiteboardStore.defaultTldrawFontId)
          return
        }

        const tldrawToolId = newTool?.tldrawToolId ?? 'draw'
        // Geo tool
        // Switch to the geo tool in Tldraw depends on the selected secondary,
        // and set the color based on geo color menu

        if (tldrawToolId === GEO_TOOL.tldrawToolId) {
          const tldrawShapeId = whiteboardStore.selectedSecondaryTool?.tldrawToolId
          if (tldrawShapeId == null) {
            return
          }
          editor.updateInstanceState(
            {
              stylesForNextShape: {
                ...editor.getInstanceState().stylesForNextShape,
                [GeoShapeGeoStyle.id]: tldrawShapeId,
              },
            },
            { history: 'ignore' },
          )
          editor.setStyleForNextShapes(DefaultSizeStyle, 's1')

          editor.setCurrentTool(GEO_TOOL.tldrawToolId)
          editor.setStyleForNextShapes(DefaultFillStyle, newToolStates.geo.fillStyle ?? 'semi')
          if (geoColorTldrawToolId.value) {
            editor.setStyleForNextShapes(DefaultColorStyle, geoColorTldrawToolId.value)
          }
          editor.setStyleForNextShapes(DefaultFontStyle, whiteboardStore.defaultTldrawFontId)
          return
        }

        if (tldrawToolId === ARROW_TOOL.tldrawToolId) {
          const tldrawShapeId = whiteboardStore.selectedSecondaryTool?.tldrawToolId
          if (tldrawShapeId == null) {
            return
          }
          // For arrows, the secondary tool id is a combination of arrowStart, dash and arrowEnd in kebab case
          const [arrowStart, dash, arrowEnd] = tldrawShapeId.split('-')
          editor.updateInstanceState(
            {
              stylesForNextShape: {
                ...editor.getInstanceState().stylesForNextShape,
                [ArrowShapeArrowheadStartStyle.id]: arrowStart,
                [DefaultDashStyle.id]: dash,
                [ArrowShapeArrowheadEndStyle.id]: arrowEnd,
              },
            },
            { history: 'ignore' },
          )

          editor.setCurrentTool(ARROW_TOOL.tldrawToolId)
          if (arrowColorTldrawToolId.value) {
            editor.setStyleForNextShapes(DefaultColorStyle, arrowColorTldrawToolId.value)
          }
          editor.setStyleForNextShapes(DefaultFontStyle, whiteboardStore.defaultTldrawFontId)
          return
        }

        // We also override the default text size in the tldraw patch to handle double clicks on canvas
        if (tldrawToolId === TEXT_TOOL.tldrawToolId) {
          const textProperties = whiteboardToolPropertiesStore.getToolProperties('text')
          editor.run(() => {
            const defaultSize = 'm1'
            editor.setStyleForNextShapes(DefaultSizeStyle, textProperties?.size ?? defaultSize)

            const isRtl = dir() === 'rtl'
            const defaultTextAlign = isRtl ? 'end' : 'start'
            editor.setStyleForNextShapes(DefaultTextAlignStyle, defaultTextAlign)
            editor.setStyleForNextShapes(DefaultFontStyle, textProperties?.font ?? whiteboardStore.defaultTldrawFontId)
          })
        } else {
          editor.setStyleForNextShapes(DefaultSizeStyle, 's1')
        }

        // Other tools
        // Switch to the tool in Tldraw, and set the color
        const color = newSecondaryTool?.tldrawToolId as TldrawDefaultColorStyleColor | undefined

        if (tldrawToolId === DRAW_NESTED_TOOL.id) {
          if (newTool == null || newSecondaryTool == null) {
            return
          }
          editor.setCurrentTool(newSecondaryTool.tldrawToolId ?? 'draw')
          const actualSecondaryToolColorId = whiteboardStore.toolCurrentColors[newTool.id]
          const tldrawToolId = getTldrawToolId(actualSecondaryToolColorId as string)
          editor.setStyleForNextShapes(DefaultColorStyle, tldrawToolId as TldrawDefaultColorStyleColor)
          editor.setStyleForNextShapes(DefaultDashStyle, 'draw')
          return
        }
        editor.setCurrentTool(tldrawToolId)
        // Otherwise drawing a shape will fill after using geo
        editor.setStyleForNextShapes(DefaultFillStyle, 'none')
        if (color) {
          editor.setStyleForNextShapes(DefaultColorStyle, color)
        }
      })
    },
    { immediate: true },
  )

  watch(
    [isReadOnlyMode, () => whiteboardModeStore.isCodeMode],
    ([isReadOnlyMode, isCodeMode]) => {
      if (isCodeMode) {
        editor.updateInstanceState({ isReadonly: true })
        whiteboardStore.selectTool(SELECT_TOOL.id)
      } else if (isReadOnlyMode) {
        editor.updateInstanceState({ isReadonly: true })
        whiteboardStore.selectTool(INTERACT_TOOL.id)
      } else {
        editor.updateInstanceState({ isReadonly: false })
        whiteboardStore.selectDefaultTool()
      }
      nextTick(() => {
        removeShortcutsListener()
        removeShortcutsListener = addShortcutsListener()
      })
    },
    { immediate: true },
  )

  watch(isAllowingZooming, (isAllowing) => {
    if (isAllowing) {
      nextTick(() => {
        forceUpdateViewportScreenBounds()
      })
    }
  })

  // Force this update to fix an issue where the zoom level is incorrect
  // when going from landscape to portrait.
  watch(deviceOrientation, forceUpdateViewportScreenBounds)

  // When page changes, the newly opened page won't have the correct zoom level.
  // So we need to re-zoom to the correct level.
  watch([currentFrameId, canvasWidth, canvasHeight], () => {
    void centerZoomToBounds()
  })

  watch(
    () => whiteboardToolbarStore.flattenedDisplayedTools,
    () => {
      removeShortcutsListener()
      removeShortcutsListener = addShortcutsListener()
    },
  )
  // When code mode is on, we need to re-zoom to the correct level after resizing the canvas.
  watch(
    () => whiteboardModeStore.isCodeMode,
    (isCodeMode) => {
      if (!isCodeMode) {
        return
      }
      forceUpdateViewportScreenBounds()
    },
  )

  /**
   * SYNC TLDRAW STATE TO VUE REACTIVE STATE
   *
   * All syncing for **global** state should be done here to ensure that
   * we only start rendering our UI after we have started listening
   * to all relevant global state to prevent race conditions.
   *
   * We do the syncing before anything else to ensure that there aren't any updates
   * to the editor state before we start listening.
   */

  // NOTE: This composable allows us to register reactive effects that depend on the Tldraw state
  // anywhere in the Vue app. From now on, you don't need to put `react` calls here anymore.
  // Calling the `set` function to signify that Tldraw has mounted and the editor instance is ready.
  useTldrawEditor().set(editor)

  // TODO: Move all these reactive effects to where they make sense e.g. in Pinia stores, relevant components, etc.

  /**
   * After using certain tools, e.g. geometry or shape, the editor will change the current tool back to Select automatically.
   * We track the change here and update our store value to match the editor value.
   *
   * Why not just check if the path starts with 'select'?
   * There are intermediate states (e.g. 'select.resizing') that the user could cancel from (e.g. Esc).
   * In those cases, we don't want to switch to Select yet to preserve the original behavior.
   * Also, we should not update the toolbar selected tool until the pointer is up.
   */
  if (!isAppUsing('sandboxRetainActiveTool')) {
    react('set store tool to Select, if needed', () => {
      if (editor.isInAny('select') && whiteboardStore.selectedToolId !== 'select') {
        whiteboardStore.selectToolWithoutTriggeringTldraw('select')
      }
    })
  } else {
    react('retain active tool after adding shape', () => {
      if (!editor.isInAny('select.pointing_canvas')) return
      const toolMap = {
        text: 'text',
        note: 'note',
        geo: 'geo',
        arrow: 'arrow',
      }
      const selectedTool = whiteboardStore.selectedToolId
      if (selectedTool in toolMap) {
        editor.setCurrentTool(toolMap[selectedTool])
      }
      if (whiteboardStore.selectedSecondaryTool?.tldrawToolId === 'padlet-speech-bubble') {
        editor.setCurrentTool('padlet-speech-bubble')
      }
    })
  }

  react('get selected object and check for script', () => {
    if (editor.isInAny('select')) {
      const selectedShapes = editor.getSelectedShapes()
      const selectedShape = selectedShapes.length === 1 ? selectedShapes[0] : null // If multiple shapes are selected, we don't show the script editor
      selectedShapeIdForScript.value = selectedShape?.id
      xOpenScriptEditorButton.value = selectedShape?.id != null
    } else {
      xOpenScriptEditorButton.value = false
    }
  })

  react('update frame state with tldraw pages data', () => {
    const pages = editor.getPages()

    const newFrames = pages
      .map((page) => {
        return {
          id: page.id,
          name: page.name,
        }
      })
      .filter((page) => !!page?.id) // to filter out the observer object before assigning to state

    // Update frames and merge with existing frames img
    whiteboardStore.frames = newFrames
  })

  react('set undo and redo capability', () => {
    const canUndo = editor.getCanUndo()
    const canRedo = editor.getCanRedo()
    whiteboardStore.canIUndo = canUndo
    whiteboardStore.canIRedo = canRedo
  })

  react('set transparent background based on current page meta', () => {
    const currentFrame = editor.getCurrentPage()
    const currentFrameMeta = currentFrame?.meta as PageMeta

    const currentBackgroundType = currentFrameMeta.backgroundType ?? BackgroundType.Plain
    isTransparentBackground.value = currentBackgroundType === BackgroundType.Frosted
  })

  react('allow drawing with finger after using stylus', () => {
    if (editor.isInAny('select.idle') && editor.getInstanceState().isPenMode) {
      editor.updateInstanceState({ isPenMode: false })
    }
  })

  react('listen to zoom level changes', () => {
    whiteboardZoomLevelStore.zoomLevel = editor.getZoomLevel()
  })

  react('listen to background changes', () => {
    // The displayedFrameBackground is outdated when the background is set from other sessions
    // and the frame background is not yet synced with the frame meta.
    // In this case, we need to sync displayedFrameBackgrounds with frame meta
    const pages = editor.getPages()
    const hasFrameBackgroundChanged = pages.some((page) => {
      const backgroundType = page.meta.backgroundType as string
      const displayedBackground = whiteboardFrameBackgroundStore.displayedFrameBackgrounds[page.id]
      return (
        displayedBackground?.id !== backgroundType ||
        displayedBackground?.url !== whiteboardFrameBackgroundStore.getPageBackgroundUrl(page)
      )
    })

    if (hasFrameBackgroundChanged) {
      whiteboardFrameBackgroundStore.syncOriginalFrameBackgroundsWithFrameMeta()
    }
  })

  whiteboardStore.selectDefaultTool()
  initializeLinking(editor)
  listenToLinkingChanges(editor)

  whiteboardFrameBackgroundStore.initializeFrameBackground(editor)

  migrateExistingTextShapesToUseThemeFont()

  useWhiteboardMediaSearchResultsCache().populateSearchResultsFromCache()

  /**
   * SHAPE APPROVAL
   */
  react('update unapproved shapes', () => {
    const editor = useTldrawEditor().editor
    if (editor == null) {
      whiteboardStore.unapprovedShapes = []
      return
    }

    const unapprovedShapes = editor
      .getPages()
      .flatMap((page) => Array.from(editor.getPageShapeIds(page.id)))
      .map((shapeId) => editor.getShape(shapeId))
      .filter((shape) => !isShapeApproved(shape)) as TLShape[]
    whiteboardStore.unapprovedShapes = cloneDeep(unapprovedShapes)
    whiteboardStore.timesUnapprovedShapesUpdated = whiteboardStore.timesUnapprovedShapesUpdated + 1
  })

  /**
   * BREAKOUT LINK / FRAME LINK
   * Do it before we update the viewport screen bounds, so the camera is correctly bounded
   */
  let frameId
  if (whiteboardStore.isFrameBreakout) {
    const urlObj = new URL(window.location.href)
    const pathSegments = urlObj.pathname.split('/')
    // frameBreakoutIdentifier format: {wallHashidWithoutPrefix}-{frameId}
    // Note: The frameId itself may contain -
    const frameIdFromPath = pathSegments[pathSegments.length - 1].replace(
      `${surfaceStore.wallHashidWithoutPrefix}-`,
      '',
    )
    // Value is not automatically decoded unlike URLSearchParams
    frameId = decodeURIComponent(frameIdFromPath)
  } else {
    // Handle link to specific frame (not breakout), frameId is a query param instead
    const searchParams = new URLSearchParams(window.location.search)
    frameId = searchParams.get(FRAME_ID_QUERY_PARAM) || ''
  }

  // Update current frame if exists
  const matchingFrame = editor.getPage(frameId as TLPageId)
  if (matchingFrame) {
    // We don't want users to be able to undo to reach the initial frame
    editor.run(
      () => {
        editor.setCurrentPage(matchingFrame)
      },
      { history: 'ignore' },
    )
  }

  /**
   * SETUP FINITE CANVAS
   */
  editor.setCameraOptions({
    ...tldrawCameraOptions.value,
  })
  void centerZoomToBounds()

  /**
   * ADD NAMES TO REALTIME CURSOR
   */
  const name =
    surfaceStore.user.display_name ??
    surfaceStore.user.short_name ??
    surfaceStore.user.name ??
    surfaceStore.user.username
  setUserPreferences({
    id: String(surfaceStore.user.id) ?? uniqueId('anon-'),
    name,
  })

  useWhiteboardPageHandlers(editor).registerSideEffects()

  /**
   * ADD NAMES TO REALTIME CURSOR
   */
  useWhiteboardShapeHandlers(editor)

  useWhiteboardCustomEventHandlers(editor)

  /**
   * CURSORS PRESENCE
   */

  const numberOfCollaborators = computedAtom('numberOfCollaborators', () => editor.getCollaborators().length)
  react('listen to collaborators to set presence count', () => {
    const presenceCount = numberOfCollaborators.get() + 1 // +1 for the current user
    useWhiteboardCollaborationStore().setPresenceCount(presenceCount)
  })

  // Upload with pasting and dropping

  editor.registerExternalContentHandler('files', async (content) => {
    if (!whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId)) return
    if (!whiteboardToolsStore.isToolEnabled(ContentPickerSource.FileBrowser)) return
    const fileInfos = await calculateUploadedFilePositionsAndDimensions({
      files:
        content.files.length > MAXIMUM_NUMBER_OF_FILES
          ? content.files.slice(0, MAXIMUM_NUMBER_OF_FILES)
          : content.files,
      dropPosition: { x: content.point?.x ?? 0, y: content.point?.y ?? 0 },
      canvasWidth: canvasWidth.value,
      canvasHeight: canvasHeight.value,
    })
    fileInfos.forEach((info) => {
      const { file, width, height, aspectRatio, x, y } = info
      whiteboardUploadStore.startNewFileUpload(file, {
        contentSource: ContentPickerSource.FileBrowser,
        x,
        y,
        width,
        height,
        aspectRatio,
      })
    })
  })

  function createShapeForTextOrUrl(text: string, point?: VecLike): void {
    try {
      const url = new URL(text)
      whiteboardUploadStore.startNewUrlUpload(url.toString(), { contentSource: ContentPickerSource.LinkInput })
    } catch {
      // Not a URL
      editor.createShape({
        type: 'text',
        props: {
          richText: toRichText(text),
        },
        x: point?.x,
        y: point?.y,
      })
    }
  }

  // Script editor/Code mode

  editor.registerExternalContentHandler('embed', async (content) => {
    createShapeForTextOrUrl(content.url, content.point)
  })

  editor.registerExternalContentHandler('text', async (content) => {
    createShapeForTextOrUrl(content.text, content.point)
  })

  editor.registerExternalContentHandler('url', async (content) => {
    createShapeForTextOrUrl(content.url, content.point)
  })

  editor.registerExternalContentHandler('svg-text', async (content) => {
    const svgFile = new File([content.text], 'svg-text.svg', { type: 'image/svg+xml' })
    whiteboardUploadStore.startNewFileUpload(svgFile, { contentSource: ContentPickerSource.FileBrowser })
  })

  // Fix: Set `isHoveringCanvas` to true on mount to ensure shape indicators update properly.
  // When Tldraw canvas doesn't fill the viewport, the pointer may already be over the canvas
  // without triggering `onPointerEnter`. This causes `hoveredShapeId` to update but indicators
  // remain hidden since `DefaultShapeIndicators` requires both `hoveredShapeId` AND `isHoveringCanvas`.
  // See: tldraw#packages/editor/src/lib/components/default-components/DefaultShapeIndicators.tsx:62
  editor.updateInstanceState({ isHoveringCanvas: true })

  useWhiteboardSpecialEffectsStore().preloadSpecialEffectsWhenInteractToolActivated()

  // Clean up failed uploads
  deleteFailedUploads()
  hasTldrawLoaded.value = true
}

useTldrawCanvas({
  elementId: 'tldraw-app',
  onMount: onTldrawCanvasMounted,
})

// Secondary Menu

const toolbar = ref<typeof WhiteboardToolbar | null>(null)
const toolbarEl = computed<HTMLElement | undefined>(() => toolbar.value?.$el)

const whiteboardToolbarStore = useWhiteboardToolbarStore()
const contentPickerStore = useContentPickerStore()

const xToolbarMenu = computed(() => {
  if (!whiteboardToolbarStore.xSecondaryMenu) return false
  if (whiteboardToolbarStore.selectedToolbarButtonEl == null) return false
  if (whiteboardStore.selectedTool != null && whiteboardStore.selectedTool.secondaryMenu != null) {
    return true
  }
  // When opening from the toolbar, we hide the content source menu in ContentPicker
  // since it's already displayed in WhiteboardToolbarMenu.vue.
  // This avoids duplicate menus while maintaining the same content panel functionality
  return (
    contentPickerStore.xContentSourceMenu &&
    contentPickerStore.contentPickerContext === 'whiteboardContentPickerOnToolbarMenu'
  )
})

// Script Editor
// Code mode
const xScriptEditor = ref(false)
const selectedShapeIdForScript = ref<string | undefined>(undefined)
const xOpenScriptEditorButton = ref(false)
const isScriptEditorEnabled = computed(() => whiteboardModeStore.isCodeMode)
const whiteboardObjectPreviewStore = useWhiteboardObjectPreviewStore()
onMounted(() => {
  // Set isScriptEditorEnabled to true if the url params include script_editor=true
  // Color should be changed before tldraw is initialized
  changeAllTldrawDefaultColorStyleColor()
  // Initialize scripts, update initial previews and initialize blockly
  whiteboardScriptStore.initializeWhiteboardScripts().then(() => {
    whiteboardObjectPreviewStore.updateObjectPreviews(Object.keys(whiteboardScriptStore.scripts) as TLShapeId[])
    if (isAppUsing('sandboxCode')) {
      import('@@/bits/blockly').then(({ initialize }) =>
        initialize({
          getCardList: () => whiteboardStore.frames.map((frame, index) => [String(index + 1), frame.id]),
        }),
      )
    }
  })
})

function saveScript(script: string) {
  if (!selectedShapeIdForScript.value) {
    return
  }
  whiteboardScriptStore.upsertScript(selectedShapeIdForScript.value, script)
  if (!whiteboardObjectPreviewStore.objectIdsWithPreviews.includes(selectedShapeIdForScript.value as TLShapeId)) {
    whiteboardObjectPreviewStore.updateObjectPreviews([selectedShapeIdForScript.value as TLShapeId])
  }
  xScriptEditor.value = false
}
const initialScript = computed(() => {
  if (!selectedShapeIdForScript.value) {
    return ''
  }
  return whiteboardScriptStore.scripts[selectedShapeIdForScript.value]
})

/**
 * CONTEXT MENU
 */

const contextMenuRef = ref<InstanceType<typeof WhiteboardElementContextMenu> | null>(null)
const contextMenuEl = computed(() => contextMenuRef.value?.menuElementRef?.$el)
const elementModifierRef = ref<InstanceType<typeof WhiteboardElementModifierContainer> | null>(null)

/**
 * All click events should dismiss the context menu. Except for:
 *
 * 1. Clicking inside the context menu itself - the WhiteboardElementContextMenu will handle this.
 * 2. Clicking on the element modifier (three-dot button, etc.). The WhiteboardElementModifier will also handle this.
 *
 * @returns true if the context menu was dismissed, false if it was preserved
 */
const checkAndDismissContextMenu = (e: PointerEvent): boolean => {
  const target: Node | null = e.target as Node | null

  // Don't dismiss context menu if clicking inside the context menu itself - let the context menu click handlers dismiss the menu instead.
  if (contextMenuEl.value?.contains(target) === true) {
    return false
  }

  // Don't dismiss context menu if clicking on the element modifier - let the WhiteboardElementModifier handle this.
  if (elementModifierRef.value?.containerEl?.contains(target) === true) {
    return false
  }

  // On anywhere else, dismiss the context menu
  whiteboardElementModifierStore.hideContextMenu()
  return true
}

const handleContainerPointerDown = (e: PointerEvent): void => {
  checkAndDismissContextMenu(e)
  whiteboardElementModifierStore.checkAndShowContentModerationModifier(e)
}

const handleTldrawAppPointerDown = (e: PointerEvent): void => {
  checkAndDismissContextMenu(e)
  whiteboardElementModifierStore.checkAndShowContentModerationModifier(e)
  whiteboardToolbarStore.hideToolbarMenu()
}

const handleToolbarPointerDown = (e: PointerEvent): void => {
  checkAndDismissContextMenu(e)
  whiteboardElementModifierStore.checkAndShowContentModerationModifier(e)
}

function handleBoardAction(actionType: ActionType, selectedShapes?: TLShape[]) {
  if (actionType === ActionType.Link) {
    openLinkingPanel()
    return
  }
  if (actionType === ActionType.SpecialEffect) {
    useWhiteboardSpecialEffectsStore().openSpecialEffectsModal()
    return
  }
  void handleAction(actionType, selectedShapes)
}

/*
 * FRAME BAR
 */

const whiteboardContainerRef = ref<HTMLElement | null>(null)
const {
  top: containerTop,
  bottom: containerBottom,
  height: containerHeight,
} = useElementBounding(whiteboardContainerRef)
const { height: toolbarHeight, top: toolbarTop } = useElementBounding(toolbarEl)
const { top: canvasTop } = useElementBounding(tldrawAppRef)

const TOOLBAR_MARGIN_TOP_MAX_PX = 80

/**
 * When AI chat is on (end-content slot), prefer 80px clearance below the header. When the
 * container is too short for that margin plus the toolbar height, shrink toward 0.
 */
const toolbarTopMarginPx = computed(() => {
  if (!xAiChat.value) return 0
  if (!Number.isFinite(containerHeight.value) || containerHeight.value <= 0) return TOOLBAR_MARGIN_TOP_MAX_PX
  if (!Number.isFinite(toolbarHeight.value) || toolbarHeight.value <= 0) return TOOLBAR_MARGIN_TOP_MAX_PX
  return Math.min(TOOLBAR_MARGIN_TOP_MAX_PX, Math.max(0, containerHeight.value - toolbarHeight.value))
})

const frameBarMaxHeight = computed(() => {
  // Height of canvas
  return Math.max(canvasHeight.value, toolbarEl.value?.getBoundingClientRect().height ?? 0)
})
const xFrameBar = computed(() => {
  return (
    !hasError.value &&
    hasTldrawLoaded.value &&
    !whiteboardStore.isFrameBreakout &&
    !whiteboardModeStore.isFramePreviewMode &&
    !whiteboardModeStore.isFullWidthFramePreviewMode &&
    !whiteboardModeStore.isScreenshotMode &&
    whiteboardModeStore.isDrawMode
  )
})
const xFrameBreakoutNavigationBar = computed(() => {
  return (
    !hasError.value &&
    hasTldrawLoaded.value &&
    whiteboardStore.isFrameBreakout &&
    !whiteboardModeStore.isFramePreviewMode &&
    !whiteboardModeStore.isFullWidthFramePreviewMode &&
    !whiteboardModeStore.isScreenshotMode &&
    surfaceContainerSizeStore.isLandscapePhone &&
    device.phone
  )
})

// Watcher to update the card template preview animation state.
watch(
  () => whiteboardPanelsStore.xCardTemplatesPanel,
  async () => {
    if (whiteboardContainerRef.value == null) return
    whiteboardCardTemplatesStore.isTemplatePreviewUIAnimating = true
    await awaitTransitionEnd(whiteboardContainerRef.value)

    // Fix an issue with `useElementBounding(tldrawAppRef)` not returning the latest top value
    // after the card templates preview animation.
    const top = tldrawAppRef.value?.getBoundingClientRect()?.top
    if (top != null) {
      canvasTop.value = top
    }

    // Wait for DOM updates to complete before marking the animation as done.
    await awaitDOMUpdates()
    whiteboardCardTemplatesStore.isTemplatePreviewUIAnimating = false
  },
)

function handleFrameBarPointerDown(): void {
  unselectAllShapesAndSwitchToDefaultTool()
  whiteboardAiStore.hideSearchInputAndCardTemplateButtons()
}

/**
 * FRAME CONTEXT MENU
 */

const xFrameContextMenu = ref(false)
const frameContextMenuAnchor = ref<Anchor>()
const frameContextMenuFrameId = ref<TLPageId>('' as TLPageId)

const showFrameContextMenu = (anchor: Anchor, frameId: TLPageId) => {
  xFrameContextMenu.value = true
  frameContextMenuAnchor.value = anchor
  frameContextMenuFrameId.value = frameId
}

/*
 * PERMISSION
 */

const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
const { isEmbedPlayMode, isReadOnlyMode } = storeToRefs(whiteboardModeStore)

/*
 * TOOLBAR
 */

const xToolbar = computed(
  () =>
    (isAppUsing('removePlayMode') ||
      whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId)) &&
    whiteboardModeStore.isDrawMode &&
    (!isReadOnlyMode.value || isEmbedPlayMode.value) &&
    !whiteboardPanelsStore.xCardTemplatesPanel &&
    !whiteboardModeStore.isScreenshotMode,
)
/*
 * AI CHAT
 */

const surfaceAIChatStore = useSurfaceAIChatStore()
const { toggleChatPanel } = surfaceAIChatStore
const { xSurfaceAIChatPanel } = storeToRefs(surfaceAIChatStore)

const toggleAIChatPanel = (): void => {
  trackEvent(
    'Surface AI chat',
    xSurfaceAIChatPanel.value ? 'Closed surface AI chat panel' : 'Opened surface AI chat panel',
    null,
    null,
    { surfaceType: 'whiteboard', source: 'Whiteboard Container Main' },
  )
  toggleChatPanel()
}

/** LINKING & LINKING PANEL */
const whiteboardLinkingStore = useWhiteboardLinkingStore()
function openLinkingPanel() {
  const tldrawEditor = useTldrawEditor().editor
  if (!tldrawEditor) return
  const shapeId = tldrawEditor.getOnlySelectedShapeId()
  if (!shapeId) return
  whiteboardLinkingStore.openLinkingPanel(shapeId)
}

/**
 * FROZEN BADGE
 */

const xFrozenBadgeMobile = computed(() => {
  return (
    isWhiteboardFrozen.value &&
    !whiteboardModeStore.isScreenshotMode &&
    !whiteboardModeStore.isFramePreviewMode &&
    !whiteboardModeStore.isFullWidthFramePreviewMode &&
    !(surfaceContainerSizeStore.isLandscapePhone && isTouchScreen()) &&
    !surfaceContainerSizeStore.isContainerBiggerThanTabletPortrait
  )
})

/**
 * PLANS
 */

const personalPlansStore = usePersonalPlansStore()

/**
 * POPOVER HEIGHT
 */

const { deviceOrientation, windowHeight } = storeToRefs(useWindowSizeStore())

const PADDING_FROM_EDGES = 12
const maxPopoverHeight = computed(() => windowHeight.value - PADDING_FROM_EDGES * 2)
</script>

<template>
  <div
    id="wall-container"
    ref="wallContainerRef"
    :class="['flex', 'relative', 'overflow-hidden', 'px-safe-inset']"
    :style="{
      // box shadows don't render well in pdfs by default; this helps to fix that issue
      '-webkit-filter': whiteboardModeStore.isScreenshotMode ? 'blur(0)' : undefined,
    }"
    @pointerdown="handleContainerPointerDown"
  >
    <WhiteboardHeader
      v-if="isAppUsing('removePlayMode') && xWhiteboardHeader"
      @background-pointerdown="handleClickingOutsideOfCanvas"
    />
    <WhiteboardHeaderOld
      v-if="!isAppUsing('removePlayMode') && xWhiteboardHeader"
      @background-pointerdown="handleClickingOutsideOfCanvas"
    />

    <!-- Mode switcher under title -->
    <!-- For mobile/tablet -->
    <WhiteboardModeSwitcher
      v-if="
        xModeSwitcher &&
        !(surfaceContainerSizeStore.isLandscapePhone && isTouchScreen()) &&
        (!surfaceContainerSizeStore.isContainerBiggerThanTabletLandscape ||
          (isTouchScreen() && surfaceContainerSizeStore.isLandscapeTablet))
      "
      :background-luminance="surfaceStore.backgroundLuminance"
      :class="['items-start', 'ms-14', 'mb-5']"
    />
    <WhiteboardHeaderFrozenBadge
      v-if="xFrozenBadgeMobile"
      :x-slash="false"
      :class="[!device.app && '-mt-2', 'mx-auto']"
    />
    <div
      ref="whiteboardContainerRef"
      :class="[
        'w-full h-full flex items-center justify-center relative box-border',
        whiteboardCardTemplatesStore.isShowingCardTemplatePreview && [
          'transition-[padding-inline-start,padding-inline-end] motion-reduce:transition-none',
          {
            'duration-300': whiteboardPanelsStore.xCardTemplatesPanel,
            'duration-200': !whiteboardPanelsStore.xCardTemplatesPanel,
          },
        ],
        !isLoadingPortraitMobileLayout && whiteboardModeStore.isDrawMode && 'mb-0 ',
        whiteboardModeStore.isCodeMode && 'items-stretch p-4 gap-4 box-border',
      ]"
      :style="{
        paddingInlineStart: `${paddingInlineStart}px`,
        paddingInlineEnd: `${paddingInlineEnd}px`,
      }"
      @pointerdown.self="handleClickingOutsideOfCanvas"
    >
      <!-- Frame bar -->

      <WhiteboardFrameBar
        v-if="xFrameBar"
        v-show="
          // When showing card templates panel, we only temporarily hide the frame bar.
          // Do not put this in v-if because we don't want to recalculate the frame bar position.
          !whiteboardPanelsStore.xCardTemplatesPanel
        "
        :container-top="containerTop"
        :container-bottom="containerBottom"
        :canvas-height="canvasHeight"
        :canvas-top="canvasTop"
        :toolbar-height="toolbarHeight"
        :toolbar-top="toolbarTop"
        :x-frame-context-menu="xFrameContextMenu"
        @show-frame-context-menu="showFrameContextMenu"
        @pointerdown="handleFrameBarPointerDown"
      />
      <template
        v-else-if="
          !hasError &&
          hasTldrawLoaded &&
          !whiteboardStore.isFrameBreakout &&
          !whiteboardModeStore.isFramePreviewMode &&
          !whiteboardModeStore.isFullWidthFramePreviewMode &&
          !whiteboardPanelsStore.xCardTemplatesPanel
        "
      >
        <WhiteboardFrameBarOld
          v-if="whiteboardModeStore.isDrawMode && !whiteboardModeStore.isScreenshotMode"
          :max-height="frameBarMaxHeight"
          :x-frame-context-menu="xFrameContextMenu"
          @show-frame-context-menu="showFrameContextMenu"
        />
      </template>

      <WhiteboardFrameBreakoutNavigationBar v-if="xFrameBreakoutNavigationBar" />
      <!-- SCRIPT EDITOR -->
      <!-- NB: Since both code editor and whiteboard canvas are div, when code editor is mounted, we need the key so that the div that is rendering the canvas is not reused to
        render the code editor. If it is reused, the tldraw-app container will be unmounted and remounted, and that it will dispose of the canvas and the canvas will be blank. -->
      <div
        v-if="isScriptEditorEnabled"
        key="code-editor"
        :class="[
          surfaceStore.backgroundLuminance === 'dark' ? 'bg-dark-ui-100' : 'bg-light-ui-100',
          'border-box rounded-2.5xl overflow-hidden grow',
        ]"
      >
        <WhiteboardCodeEditor
          :selected-shape-id="selectedShapeIdForScript"
          :initial-script="initialScript"
          @save="saveScript"
          @cancel="xScriptEditor = false"
        />
      </div>

      <div
        key="whiteboard-canvas"
        :class="[
          'relative',
          whiteboardModeStore.isCodeMode ? 'flex flex-col box-border' : '',
          whiteboardCardTemplatesStore.isShowingCardTemplatePreview && [
            'transition-[width,height,inset-inline-start,top,transform] motion-reduce:transition-none',
            {
              'duration-300': whiteboardPanelsStore.xCardTemplatesPanel,
              'duration-200': !whiteboardPanelsStore.xCardTemplatesPanel,
            },
          ],
        ]"
        :style="{
          width: canvasWidth + 'px',
          height: whiteboardModeStore.isCodeMode ? '100%' : canvasHeight + 'px',
          insetInlineStart: insetInlineStart + 'px',
          top: whiteboardModeStore.isCodeMode ? '0' : insetTop + 'px',
          ...(shouldTransformCanvasPerspectiveAngle
            ? {
                transform:
                  dir() === 'rtl'
                    ? `perspective(${CANVAS_TEMPLATE_PREVIEW_PERSPECTIVE}px) rotateY(-${CANVAS_TEMPLATE_PREVIEW_ROTATE_Y_DEGREE}deg)`
                    : `perspective(${CANVAS_TEMPLATE_PREVIEW_PERSPECTIVE}px) rotateY(${CANVAS_TEMPLATE_PREVIEW_ROTATE_Y_DEGREE}deg)`,
                transformOrigin: dir() === 'rtl' ? 'left center' : 'right center',
              }
            : {}),
        }"
      >
        <!-- NB: we have to force ltr for canvas to apply transform correctly -->
        <div
          dir="ltr"
          :class="[
            'overflow-hidden relative',
            !whiteboardModeStore.isFullWidthFramePreviewMode && 'rounded-xl',
            whiteboardModeStore.isCodeMode ? 'shrink-0' : '',
            whiteboardCardTemplatesStore.isShowingCardTemplatePreview && [
              'transition-[width,height] motion-reduce:transition-none',
              {
                'duration-300': whiteboardPanelsStore.xCardTemplatesPanel,
                'duration-200': !whiteboardPanelsStore.xCardTemplatesPanel,
              },
            ],
          ]"
          :style="{ width: canvasWidth + 'px', height: canvasHeight + 'px' }"
        >
          <WhiteboardCanvasDragAndDropOverlay v-if="isDragging" />
          <!-- Clicking on the tldraw canvas doesn't trigger pointerdown on the wallContainer, so we need to add it here too -->
          <div
            id="tldraw-app"
            ref="tldrawAppRef"
            tabindex="-1"
            v-emit-long-press="defaultTldrawOptions.longPressDurationMs"
            :class="[
              'w-full h-full',
              {
                'transparent-background': isTransparentBackground,
              },
            ]"
            :style="
              // Safari has a weird issue where it doesn't clear the canvas content when switching
              // between frames (See this issue: https://github.com/tldraw/tldraw/issues/5842)
              // The workaround is to set this CSS property.
              device.safari && device.mobile
                ? {
                    '-webkit-transform': 'translateZ(0)',
                  }
                : {}
            "
            @long-press.prevent="whiteboardElementModifierStore.buildContextMenu"
            @contextmenu.prevent="whiteboardElementModifierStore.buildContextMenu"
            @pointerdown="handleTldrawAppPointerDown"
          />
        </div>
        <WhiteboardCodeFrameObjectPicker
          v-if="!hasError && hasTldrawLoaded && whiteboardModeStore.isCodeMode"
          class="flex-1"
          :selected-shape-id-for-script="selectedShapeIdForScript"
          @switch-frame="switchFrame"
        />
        <WhiteboardStartFromTemplateButton v-if="whiteboardCardTemplatesStore.canShowStartFromATemplateButton" />
      </div>

      <template v-if="hasTldrawLoaded">
        <WhiteboardElementContextMenu
          v-if="xContextMenu && contextMenuAnchor"
          ref="contextMenuRef"
          :boundary="wallContainerRef"
          :max-height="maxPopoverHeight"
        />
        <ContentPickerForWhiteboard v-if="whiteboardModeStore.isDrawMode" />

        <WhiteboardToolbar
          v-if="xToolbar && !hasError"
          ref="toolbar"
          :toolbar-top-margin-px="toolbarTopMarginPx"
          @pointerdown="handleToolbarPointerDown"
        >
          <template v-if="xAiChat && whiteboardToolbarStore.isSingleColumnLayout" #end-content>
            <SurfaceAIChatButton
              class="mt-4 self-end"
              :label="__('AI teaching assistant chat')"
              :dark-mode="surfaceStore.isSkinPrimaryColorDark"
              :line-color="surfaceStore.skinAccentColorAsRGB"
              :background="aiButtonColors?.background"
              :text-color="aiButtonColors?.text"
              :hover-background="aiButtonColors?.hoverBackground"
              :hover-text-color="aiButtonColors?.hoverText"
              @click="toggleAIChatPanel"
            />
          </template>
        </WhiteboardToolbar>
        <WhiteboardAiChatContainer v-if="whiteboardAiStore.xSandboxAIChat" class="absolute bottom-0 end-0" />
        <!-- Secondary menu(s) -->
        <!-- Place it at top level so that it can render below the toolbar -->
        <WhiteboardToolbarMenu
          v-if="xToolbarMenu"
          :canvas-height="canvasHeight"
          :container-bottom="containerBottom"
          :container-height="containerHeight"
          :toolbar-height="toolbarHeight"
          :toolbar-top="toolbarTop"
        />

        <WhiteboardElementModifierContainer
          v-if="whiteboardElementModifierStore.selectedShapes.length > 0"
          ref="elementModifierRef"
        />
      </template>
    </div>

    <!-- PLAY MODE NAVIGATION BAR -->
    <WhiteboardPlayModeNavigationBar
      v-if="whiteboardModeStore.isPlayMode && !whiteboardModeStore.isScreenshotMode && !whiteboardStore.isFrameBreakout"
      ref="whiteboardPlayModeNavigationBarRef"
    />
    <!-- FRAME CONTEXT MENU -->
    <!-- Render here instead of inside the frame bar component because we want the scrim to apply to the entire page, not just the frame bar -->
    <WhiteboardFrameContextMenu
      v-if="xFrameContextMenu && frameContextMenuAnchor"
      :anchor="frameContextMenuAnchor"
      :boundary="wallContainerRef"
      :max-height="maxPopoverHeight"
      :frame-id="frameContextMenuFrameId"
      :dark-mode="surfaceStore.backgroundLuminance === 'dark'"
      @close="xFrameContextMenu = false"
    />

    <!-- PANELS -->

    <Teleport to="body">
      <template v-if="whiteboardStore.xFrameBackgroundPanel">
        <WhiteboardFrameBackgroundPanel v-if="isAppUsing('sandboxThemeBackgrounds')" />
        <WhiteboardFrameBackgroundPanelOld v-else />
      </template>
      <WhiteboardFrameSharePanel v-if="whiteboardStore.xFrameSharePanel" />
      <WhiteboardFrameShareViaAnotherAppPanel v-if="whiteboardStore.xFrameShareViaAnotherAppPanel" />
      <WhiteboardFrameBreakoutLinksPanel v-if="whiteboardStore.xFrameBreakoutLinksPanel" />
      <WhiteboardLinkingPanel v-if="whiteboardLinkingStore.xLinkingPanel" />

      <MembershipTierUpgradeModal
        v-if="personalPlansStore.xUpgradeModal"
        :user="surfaceStore.user"
        :whiteboard-frames-used="whiteboardStore.frames.length"
        :file-that-exceeded-size="whiteboardUploadStore.fileThatExceededSize"
        @cancel="personalPlansStore.closePlanUpgradeModal"
      />
    </Teleport>

    <!-- WHITEBOARD EXPORT -->
    <!-- Banksy looks for WHITEBOARD_ATTACHMENTS_LOADED_ID to determine when the whiteboard is ready for export. -->
    <!-- We mark the whiteboard as ready for export as soon as all attachments have either been loaded, or have errored. -->
    <!-- We don't want errored attachments to block the whiteboard export. -->
    <!-- NOTE: We also need to wait for Tldraw app to render first. -->
    <div
      v-if="hasTldrawLoaded && whiteboardAttachmentsStore.hasLoadedOrErroredAllAttachments"
      :id="WHITEBOARD_ATTACHMENTS_LOADED_ID"
      data-testid="whiteboardContentLoaded"
      :aria-hidden="true"
    />
  </div>
</template>

<style lang="postcss">
@import url('tldraw-root/tldraw.css');
.tl-text {
  text-shadow: none !important;
}

/* Shantell Sans */
.tl-text-wrapper[data-font='draw'] .tl-text {
  font-feature-settings: 'calt', 'ss01', 'ss02', 'ss03', 'ss04', 'ss05', 'ss06', 'ss07', 'ss08', 'ss09', 'liga';
}

/* Inter */
.tl-text-wrapper[data-font='sans'] .tl-text {
  font-feature-settings: 'ss01', 'cv08', 'calt';
}

:root {
  .tl-theme__light {
    --color-selection-stroke: #9466e8 !important;
    --color-selection-fill: rgba(148, 102, 232, 0.24) !important;
    --color-selected: #9466e8 !important;
  }
  --safe-area-inset-top: env(safe-area-inset-top);
  --safe-area-inset-right: env(safe-area-inset-right);
  --safe-area-inset-bottom: env(safe-area-inset-bottom);
  --safe-area-inset-left: env(safe-area-inset-left);
}

/* Override style from _textfield.scss */
textarea {
  box-shadow: none !important;
}

/* Override tldraw default background-color */
#tldraw-app.transparent-background .tl-background {
  background-color: transparent !important;
}

.tl-corner-handle {
  rx: 50%;
}

/*
Apply a default font-weight of 500 to all fonts except Courier Prime (mono font).
The tl-rich-text class is included because tldraw uses it internally to measure text dimensions.
See renderHtmlFromRichTextForMeasurement() which wraps HTML in this class.
@see https://github.com/tldraw/tldraw/blob/3bf31007c5a7274f3f7926a84c96c89a4cc2c278/packages/tldraw/src/lib/utils/text/richText.ts#L87C17-L87C53
*/
.tl-text-wrapper[data-font='sans'],
.tl-text-wrapper[data-font='draw'],
.tl-text-wrapper[data-font='serif'],
.tl-rich-text {
  font-weight: 500; /* goodcheck-disable-line */
}
.tl-text-wrapper[data-font='mono'] {
  font-weight: 400;
}

.tl-text-wrapper,
.tl-rich-text {
  ul,
  ol {
    padding-left: 2ch;

    p {
      display: inline;
    }
  }

  &[data-align='start'] {
    ul,
    ol {
      &[dir='ltr'] {
        padding-inline-start: 2ch;
      }

      &[dir='rtl'] {
        list-style-position: inside;
      }
    }
  }

  &[data-align='middle'] {
    ul,
    ol {
      list-style-position: inside;
      padding-left: 0;
      text-align: center;
    }
  }

  &[data-align='end'] {
    ul,
    ol {
      text-align: right;
      padding-left: 0;
      list-style-position: inside; /* Needed in LTR mode as there are no dir="ltr" attributes */

      &[dir='ltr'] {
        list-style-position: inside;
      }

      &[dir='rtl'] {
        list-style-position: outside;
        padding-inline-start: 2ch;
      }
    }
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  strong {
    font-weight: 700; /* goodcheck-disable-line */
  }
}
</style>

<style>
/* This is combined with `pointer-events: none` that Tldraw always sets on the `HTMLContainer` component
 * (see PadletAttachmentShape.tsx) to ensure Padlet attachment shapes capture all pointer events and not
 * let them passed through to the shapes behind it in the z-axis (if any). It's a similar technique that
 * Tldraw uses for filled shapes like Geo or Note.
 * Fixes PRO-13977, where clicking on a Padlet attachment shape would still trigger the play button on an
 * audio shape behind it.
 */
.tl-canvas:is([data-iseditinganything='true'], [data-isselectinganything='true'])
  .tl-shape[data-shape-type='padlet-attachment-shape'] {
  pointer-events: all;
}
</style>
